import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    Badge,
    Menu,
    MenuItem,
    Box,
    InputBase,
    Container,
    Chip,
    Avatar,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    ShoppingCart,
    AccountCircle,
    Search,
    Favorite,
    Menu as MenuIcon,
    Store,
    Close,
    Home,
    Storefront,
    Announcement,
    Build,
    Star,
    ContactMail,
    AdminPanelSettings,
    Inventory
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, isAuthenticated } = useAuth();
    const { settings, loading } = useSettings();
    const [anchorEl, setAnchorEl] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Fonction pour vérifier si un path est actif
    const isActive = (path) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(path);
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        logout();
        handleClose();
        setMobileMenuOpen(false);
        navigate('/');
    };

    const handleMobileMenuToggle = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const handleMobileMenuClose = () => {
        setMobileMenuOpen(false);
    };

    const handleMobileNavigation = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
        }
    };

    // Menu items pour la navigation
    const menuItems = [
        { label: 'Accueil', path: '/', icon: <Home /> },
        { label: 'Produits', path: '/products', icon: <Store /> },
        { label: 'Boutiques', path: '/boutiques', icon: <Storefront /> },
        { label: 'Annonces', path: '/annonces', icon: <Announcement /> },
        { label: 'Services', path: '/services', icon: <Build /> },
        { label: 'VIP', path: '/vip', icon: <Star /> },
        { label: 'Contact', path: '/contact', icon: <ContactMail /> },
    ];

    // Ajouter Administration si l'utilisateur est admin
    if (user?.role === 'ADMIN') {
        menuItems.push({ label: 'Administration', path: '/admin', icon: <AdminPanelSettings /> });
    }

    // Ajouter Vendeur si l'utilisateur est vendeur 
    if (user?.role === 'VENDOR') {
        menuItems.push({ label: 'Mon Espace Vendeur', path: '/vendeur', icon: <Store /> });
    }


    return (
        <>
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    background: 'linear-gradient(90deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
                    color: 'text.primary',
                    borderBottom: '0px solid #e0e0e0',
                    borderRadius:0,
                }}
            >
                <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
                    <Toolbar sx={{ py: 1, px: { xs: 0, sm: 0 } }}>
                        {/* Logo */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                mr: 4,
                                px: 0,
                                pl: 0,
                                pr: 0,
                                py: 0.5,
                                borderRadius: 1,

                                transition: 'background 0.2s ease'
                            }}
                            onClick={() => navigate('/')}
                        >
                            {!loading && settings.site_logo ? (
                                <img 
                                    src={settings.site_logo} 
                                    alt="Logo" 
                                    style={{ 
                                        height: '60px', 
                                        width: '70px', 
                                        borderRadius: '20%',
                                        marginRight: '8px',
                                        objectFit: 'cover'
                                    }} 
                                />
                            ) : (
                                <Store sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                            )}
                            <Typography
                                variant="h5"
                                component="div"
                                sx={{
                                    fontWeight: 700,
                                    background: 'linear-gradient(45deg,rgb(255, 255, 255),rgb(255, 255, 255))',
                                    backgroundClip: 'text',
                                    fontSize: '25 px',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent'
                                }}
                            >
                                {!loading && settings.site_name ? settings.site_name : 'Tchamba Marketplace'}
                            </Typography>
                        </Box>

                        {/* Navigation Links */}
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, mr: 2,justifyContent: 'center',fontSize: '19px', flexGrow: 1 }}>
                            <Button
                                color="inherit"
                                onClick={() => navigate('/products')}
                                sx={{
                                    textTransform: 'none',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '17px',
                                    background: isActive('/products') ? 'rgba(254, 249, 249, 0.4)' : 'transparent',
                                    '&:hover': { background: 'rgba(254, 249, 249, 0.4)' }
                                }}
                            >
                                Produits
                            </Button>
                            <Button
                                color="inherit"
                                onClick={() => navigate('/boutiques')}
                                sx={{
                                    textTransform: 'none',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '17px',
                                    background: isActive('/boutiques') ? 'rgba(254, 249, 249, 0.4)' : 'transparent',
                                    '&:hover': { background: 'rgba(254, 249, 249, 0.4)' }
                                }}
                            >
                                Boutiques
                            </Button>
                            <Button
                                color="inherit"
                                onClick={() => navigate('/services')}
                                sx={{
                                    textTransform: 'none',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '17px',
                                    background: isActive('/services') ? 'rgba(254, 249, 249, 0.4)' : 'transparent',
                                    '&:hover': { background: 'rgba(254, 249, 249, 0.4)' }
                                }}
                            >
                                Services
                            </Button>
                            <Button
                                color="inherit"
                                onClick={() => navigate('/vip')}
                                sx={{
                                    textTransform: 'none',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '17px',
                                    background: isActive('/vip') ? 'rgba(254, 249, 249, 0.4)' : 'transparent',
                                    '&:hover': { background: 'rgba(254, 249, 249, 0.4)' }
                                }}
                            >
                                VIP
                            </Button>
                            <Button
                                color="inherit"
                                onClick={() => navigate('/annonces')}
                                sx={{
                                    textTransform: 'none',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '17px',
                                    background: isActive('/annonces') ? 'rgba(254, 249, 249, 0.4)' : 'transparent',
                                    '&:hover': { background: 'rgba(254, 249, 249, 0.4)' }
                                }}
                            >
                                Annonces
                            </Button>

                            <Button
                                color="inherit"
                                onClick={() => navigate('/contact')}
                                sx={{
                                    textTransform: 'none',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '17px',
                                    background: isActive('/contact') ? 'rgba(254, 249, 249, 0.4)' : 'transparent',
                                    '&:hover': { background: 'rgba(254, 249, 249, 0.4)' }
                                }}
                            >
                                Contact
                            </Button>
                            {user?.role === 'ADMIN' && (
                                <Button
                                    color="inherit"
                                    onClick={() => navigate('/admin')}
                                    sx={{
                                        textTransform: 'none',
                                        color: 'white',
                                        fontWeight: 700,
                                        fontSize: '17px',
                                        background: isActive('/admin') ? 'rgba(254, 249, 249, 0.4)' : 'transparent',
                                        '&:hover': { background: 'rgba(254, 249, 249, 0.4)' }
                                    }}
                                >
                                    Administration
                                </Button>
                            )}
                        </Box>

                        {/* Search Bar */}
                        {/* <Box
                            component="form"
                            onSubmit={handleSearch}
                            sx={{
                                flexGrow: 1,
                                maxWidth: { xs: '100%', sm: 400, md: 500 },
                                display: { xs: 'none', sm: 'flex' },
                                mx: { xs: 0, sm: 2 }
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 2,
                                    px: 2,
                                    py: 0.5,
                                    width: '100%',
                                    '&:hover': {
                                        backgroundColor: '#eeeeee'
                                    },
                                    '&:focus-within': {
                                        backgroundColor: 'white',
                                        boxShadow: '0 0 0 2px #1976d2'
                                    }
                                }}
                            >
                                <Search sx={{ color: 'text.secondary', mr: 1 }} />
                                <InputBase
                                    placeholder="Rechercher des produits..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    sx={{ flexGrow: 1 }}
                                />
                            </Box>
                        </Box>
                        */}
                        {/* Right Side Actions */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'flex-end' }}>
                            {isAuthenticated ? (
                                <>
                                    {/* <IconButton
                                        color="inherit"
                                        onClick={() => navigate('/favorites')}
                                        sx={{
                                            color: 'text.secondary',
                                            '&:hover': { color: 'error.main' }
                                        }}
                                    >
                                        <Favorite />
                                    </IconButton>

                                    <IconButton
                                        color="inherit"
                                        onClick={() => navigate('/cart')}
                                        sx={{
                                            color: 'text.secondary',
                                            '&:hover': { color: 'primary.main' }
                                        }}
                                    >
                                        <Badge badgeContent={0} color="error">
                                            <ShoppingCart />
                                        </Badge>
                                    </IconButton> */}

                                    <IconButton
                                        onClick={handleMenu}
                                        sx={{ ml: 0 }}
                                    >
                                        <Avatar
                                            src={user?.profilePhoto || undefined}
                                            sx={{
                                                width: 25,
                                                height: 25,
                                                bgcolor: 'primary.main',
                                                fontSize: '0.875rem'
                                            }}
                                            alt={user?.name || 'User'}
                                        >
                                            {!user?.profilePhoto && (user?.name?.charAt(0)?.toUpperCase() || 'U')}
                                        </Avatar>
                                    </IconButton>

                                    <Menu
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl)}
                                        onClose={handleClose}
                                        PaperProps={{
                                            elevation: 3,
                                            sx: {
                                                mt: 1,
                                                minWidth: 200,
                                                '& .MuiMenuItem-root': {
                                                    px: 2,
                                                    py: 1
                                                }
                                            }
                                        }}
                                    >
                                        <MenuItem onClick={() => { navigate('/profile'); handleClose(); }}>
                                            <AccountCircle sx={{ mr: 1 }} />
                                            Mon Profil
                                        </MenuItem>
                                        {user?.role === 'VENDOR' && (
                                            <MenuItem onClick={() => { navigate('/vendeur'); handleClose(); }}>
                                                Mon Dashboard
                                            </MenuItem>
                                        )}
                                        

                                        <MenuItem onClick={handleLogout}>
                                            Déconnexion
                                        </MenuItem>
                                    </Menu>
                                </>
                            ) : (
                                <Box sx={{
                                    display: { xs: 'none', sm: 'flex' },
                                    gap: 1
                                }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate('/login')}
                                        sx={{
                                            textTransform: 'none',
                                            borderColor: 'white',
                                            borderRadius: '20px',
                                            fontSize: '17px',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'rgba(254, 249, 249, 0.4)',
                                                borderColor: 'white',
                                            }
                                        }}
                                    >
                                        Connexion
                                    </Button>
                                </Box>
                            )}

                            {/* Mobile Menu Button */}
                            <IconButton
                                sx={{ 
                                    display: { xs: 'block', md: 'none' }, 
                                    ml: 0
                                }}
                                color="inherit"
                                onClick={handleMobileMenuToggle}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Mobile Drawer */}
            <Drawer
                anchor="right"
                open={mobileMenuOpen}
                onClose={handleMobileMenuClose}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 280,
                        backgroundColor: 'white',
                    },
                }}
            >
                <Box sx={{ p: 2 }}>
                    {/* Header du drawer */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.color?.degradeOne }}>
                           Navigation
                        </Typography>
                        <IconButton onClick={handleMobileMenuClose}>
                            <Close />
                        </IconButton>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Navigation Links */}
                    <List>
                        {menuItems.map((item) => (
                            <ListItem key={item.path} disablePadding>
                                <ListItemButton
                                    onClick={() => handleMobileNavigation(item.path)}
                                    sx={{
                                        borderRadius: 1,
                                        mb: 0.5,
                                        backgroundColor: isActive(item.path) ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                        }
                                    }}
                                >
                                    {/* <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>
                                        {item.icon}
                                    </ListItemIcon> */}
                                    <ListItemText
                                        primary={item.label}
                                        sx={{
                                            '& .MuiListItemText-primary': {
                                                fontWeight: 700,
                                                color: 'text.primary'
                                            }
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>

                    <Divider sx={{ my: 2 }} />

                    {/* Actions utilisateur */}
                    {isAuthenticated ? (
                        <List>
                            {/* <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => handleMobileNavigation('/favorites')}
                                    sx={{ borderRadius: 1, mb: 0.5 }}
                                >
                                    {/* <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>
                                        <Favorite />
                                    </ListItemIcon> */}
                            {/* 
                                    <ListItemText primary="Favoris" />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => handleMobileNavigation('/cart')}
                                    sx={{ borderRadius: 1, mb: 0.5 }}
                                >
                                    {/* <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>
                                        <ShoppingCart />
                                    </ListItemIcon> */}
                            {/* 
                                    <ListItemText primary="Panier" />
                                </ListItemButton>
                            </ListItem> */}
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => handleMobileNavigation('/profile')}
                                    sx={{ borderRadius: 1, mb: 0.5 }}
                                >
                                    {/* <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>
                                        <AccountCircle />
                                    </ListItemIcon> */}
                                    <ListItemText primary="Mon Profil" />
                                </ListItemButton>
                            </ListItem>
                            
                            <Divider sx={{ my: 1 }} />
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={handleLogout}
                                    sx={{ borderRadius: 1, color: 'error.main' }}
                                >
                                    <ListItemText primary="Déconnexion" />
                                </ListItemButton>
                            </ListItem>
                        </List>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                            <Box
                                sx={{
                                    background: theme.palette.color.degradeOne,
                                    padding: '1px',
                                    borderRadius: '5px',
                                }}
                            >
                                <Button
                                    // variant="outlined"
                                    fullWidth
                                    onClick={() => handleMobileNavigation('/login')}
                                    sx={{ 
                                        textTransform: 'none',
                                        borderRadius: '5px',
                                        background: 'white',
                                        color: '#f97316',
                                    }}
                                >
                                    Connexion
                                </Button>
                            </Box>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={() => handleMobileNavigation('/register')}
                                sx={{ 
                                    textTransform: 'none',
                                    background: theme.palette.color?.degradeOne || 'linear-gradient(90deg, #f97316, #ef4444, #ec4899)',
                                    color: 'white',
                                    '&:hover': {
                                        background: theme.palette.color?.degradeOne || 'linear-gradient(90deg, #f97316, #ef4444, #ec4899)',
                                        opacity: 0.9
                                    }
                                }}
                            >
                                Inscription
                            </Button>
                        </Box>
                    )}

                    {/* Barre de recherche mobile */}
                    {/* <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                            Rechercher
                        </Typography>
                        <Box
                            component="form"
                            onSubmit={handleSearch}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: '#f5f5f5',
                                borderRadius: 1,
                                px: 2,
                                py: 1,
                                '&:hover': {
                                    backgroundColor: '#eeeeee'
                                },
                                '&:focus-within': {
                                    backgroundColor: 'white',
                                    boxShadow: '0 0 0 2px #1976d2'
                                }
                            }}
                        >
                            <Search sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                            <InputBase
                                placeholder="Rechercher..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{ flexGrow: 1, fontSize: '0.875rem' }}
                            />
                        </Box>
                    </Box> */}
                </Box>
            </Drawer>
        </>
    );
};

export default Navbar;
